import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Stethoscope, Layers, Pencil, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';

type Tab = 'especialidades' | 'procedimientos';

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex flex-col justify-end md:justify-center md:items-center md:p-4">
      <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-text-primary">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function CatalogosPage() {
  const { usuario } = useAuthStore();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('especialidades');

  const canWrite = usuario?.rolId === ROL.SUPER_ADMIN || usuario?.rolId === ROL.ADMIN_CLINICA;

  // Especialidades
  const [showEspModal, setShowEspModal] = useState(false);
  const [editEsp, setEditEsp] = useState<any>(null);
  const [espForm, setEspForm] = useState({ nombre: '', descripcion: '' });

  const { data: especialidades = [] } = useQuery({
    queryKey: ['especialidades'],
    queryFn: () => api.get('/api/catalogos/especialidades').then(r => r.data),
  });

  const createEsp = useMutation({
    mutationFn: (dto: any) => api.post('/api/catalogos/especialidades', dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['especialidades'] }); toast.success('Especialidad creada'); closeEspModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear especialidad')),
  });

  const updateEsp = useMutation({
    mutationFn: ({ id, dto }: any) => api.patch(`/api/catalogos/especialidades/${id}`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['especialidades'] }); toast.success('Especialidad actualizada'); closeEspModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar especialidad')),
  });

  function openEspCreate() { setEspForm({ nombre: '', descripcion: '' }); setEditEsp(null); setShowEspModal(true); }
  function openEspEdit(e: any) { setEspForm({ nombre: e.nombre, descripcion: e.descripcion || '' }); setEditEsp(e); setShowEspModal(true); }
  function closeEspModal() { setShowEspModal(false); setEditEsp(null); }
  function submitEsp() {
    if (!espForm.nombre.trim()) return toast.error('El nombre es requerido');
    if (editEsp) updateEsp.mutate({ id: editEsp.id, dto: espForm });
    else createEsp.mutate(espForm);
  }

  // Procedimientos
  const [showProcModal, setShowProcModal] = useState(false);
  const [editProc, setEditProc] = useState<any>(null);
  const [filterEspId, setFilterEspId] = useState('');
  const [procForm, setProcForm] = useState({ nombre: '', especialidadId: '', descripcion: '', duracionMin: '', precio: '' });

  const { data: procedimientos = [] } = useQuery({
    queryKey: ['procedimientos', filterEspId],
    queryFn: () => api.get('/api/catalogos/procedimientos', { params: filterEspId ? { especialidadId: filterEspId } : {} }).then(r => r.data),
  });

  const createProc = useMutation({
    mutationFn: (dto: any) => api.post('/api/catalogos/procedimientos', dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['procedimientos'] }); toast.success('Procedimiento creado'); closeProcModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear procedimiento')),
  });

  const updateProc = useMutation({
    mutationFn: ({ id, dto }: any) => api.patch(`/api/catalogos/procedimientos/${id}`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['procedimientos'] }); toast.success('Procedimiento actualizado'); closeProcModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar procedimiento')),
  });

  function openProcCreate() {
    setProcForm({ nombre: '', especialidadId: filterEspId, descripcion: '', duracionMin: '', precio: '' });
    setEditProc(null);
    setShowProcModal(true);
  }
  function openProcEdit(p: any) {
    setProcForm({ nombre: p.nombre, especialidadId: p.especialidadId || '', descripcion: p.descripcion || '', duracionMin: p.duracionMin?.toString() || '', precio: p.precio?.toString() || '' });
    setEditProc(p);
    setShowProcModal(true);
  }
  function closeProcModal() { setShowProcModal(false); setEditProc(null); }
  function submitProc() {
    if (!procForm.nombre.trim()) return toast.error('El nombre es requerido');
    const dto = {
      ...procForm,
      duracionMin: procForm.duracionMin ? parseInt(procForm.duracionMin) : null,
      precio: procForm.precio ? parseFloat(procForm.precio) : null,
    };
    if (editProc) updateProc.mutate({ id: editProc.id, dto });
    else createProc.mutate(dto);
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'especialidades', label: 'Especialidades', icon: <Stethoscope size={15} /> },
    { id: 'procedimientos', label: 'Procedimientos', icon: <Layers size={15} /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl md:text-2xl font-bold text-text-primary">Catálogos</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              tab === t.id ? 'bg-white text-primary-700 shadow-sm' : 'text-text-secondary hover:text-text-primary'
            )}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* Especialidades */}
      {tab === 'especialidades' && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <p className="text-sm font-medium text-text-secondary">{especialidades.length} especialidades</p>
            {canWrite && (
              <button onClick={openEspCreate} className="hidden md:flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
                <Plus size={15} /> Nueva especialidad
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-50">
            {especialidades.length === 0 && (
              <p className="text-center py-10 text-text-secondary text-sm">Sin especialidades registradas</p>
            )}
            {especialidades.map((e: any) => (
              <div key={e.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/50">
                <div>
                  <p className="text-sm font-medium text-text-primary">{e.nombre}</p>
                  {e.descripcion && <p className="text-xs text-text-secondary mt-0.5">{e.descripcion}</p>}
                </div>
                {canWrite && (
                  <button onClick={() => openEspEdit(e)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Procedimientos */}
      {tab === 'procedimientos' && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50 gap-4">
            <select
              value={filterEspId}
              onChange={e => setFilterEspId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="">Todas las especialidades</option>
              {especialidades.map((e: any) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
            {canWrite && (
              <button onClick={openProcCreate} className="hidden md:flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
                <Plus size={15} /> Nuevo procedimiento
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-50">
            {procedimientos.length === 0 && (
              <p className="text-center py-10 text-text-secondary text-sm">Sin procedimientos registrados</p>
            )}
            {procedimientos.map((p: any) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">{p.nombre}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {p.especialidad && <span className="text-xs text-text-secondary">{p.especialidad.nombre}</span>}
                    {p.duracionMin && <span className="text-xs text-text-secondary">{p.duracionMin} min</span>}
                    {p.precio != null && (
                      <span className="text-xs font-medium text-primary-600">
                        ${p.precio.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                </div>
                {canWrite && (
                  <button onClick={() => openProcEdit(p)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary ml-3">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAB mobile */}
      {canWrite && tab === 'especialidades' && (
        <button
          onClick={openEspCreate}
          className="md:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center"
          style={{ boxShadow: '0 4px 16px rgba(14,133,191,0.4)' }}
          aria-label="Nueva especialidad"
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}
      {canWrite && tab === 'procedimientos' && (
        <button
          onClick={openProcCreate}
          className="md:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center"
          style={{ boxShadow: '0 4px 16px rgba(14,133,191,0.4)' }}
          aria-label="Nuevo procedimiento"
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}

      {/* Modal especialidad */}
      {showEspModal && (
        <Modal title={editEsp ? 'Editar especialidad' : 'Nueva especialidad'} onClose={closeEspModal}>
          <div className="space-y-4">
            <Field label="Nombre *">
              <input className={inputCls} value={espForm.nombre} onChange={e => setEspForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Cardiología" />
            </Field>
            <Field label="Descripción">
              <textarea className={cn(inputCls, 'resize-none')} rows={3} value={espForm.descripcion} onChange={e => setEspForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Descripción opcional" />
            </Field>
            <div className="flex gap-3 pt-2">
              <button onClick={closeEspModal} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={submitEsp} disabled={createEsp.isPending || updateEsp.isPending} className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                {editEsp ? 'Guardar cambios' : 'Crear especialidad'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal procedimiento */}
      {showProcModal && (
        <Modal title={editProc ? 'Editar procedimiento' : 'Nuevo procedimiento'} onClose={closeProcModal}>
          <div className="space-y-4">
            <Field label="Nombre *">
              <input className={inputCls} value={procForm.nombre} onChange={e => setProcForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Extracción dental" />
            </Field>
            <Field label="Especialidad">
              <select className={inputCls} value={procForm.especialidadId} onChange={e => setProcForm(f => ({ ...f, especialidadId: e.target.value }))}>
                <option value="">Sin especialidad</option>
                {especialidades.map((e: any) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Duración (min)">
                <input className={inputCls} type="number" min={1} value={procForm.duracionMin} onChange={e => setProcForm(f => ({ ...f, duracionMin: e.target.value }))} placeholder="30" />
              </Field>
              <Field label="Precio (MXN)">
                <input className={inputCls} type="number" min={0} step="0.01" value={procForm.precio} onChange={e => setProcForm(f => ({ ...f, precio: e.target.value }))} placeholder="500.00" />
              </Field>
            </div>
            <Field label="Descripción">
              <textarea className={cn(inputCls, 'resize-none')} rows={2} value={procForm.descripcion} onChange={e => setProcForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Descripción opcional" />
            </Field>
            <div className="flex gap-3 pt-2">
              <button onClick={closeProcModal} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={submitProc} disabled={createProc.isPending || updateProc.isPending} className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                {editProc ? 'Guardar cambios' : 'Crear procedimiento'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
